import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Compass,
  Smartphone,
  Banknote,
  Home as HomeIcon,
  Users,
  Heart,
  MessageSquare,
  Sun,
  Phone,
} from "lucide-react";
import { lightTap } from "../lib/haptics";

/**
 * Leaving Safely Checklist — persistent, trauma-informed checklist covering
 * every phase of leaving (before, tech, financial, home, kids/pets, emotional,
 * contact, after).
 *
 * Each item optionally exposes:
 *   • "Why this matters" — small expandable rationale below the item.
 *   • sub-items — informational list examples (documents, apps, etc.).
 *
 * Storage key: `ss_lsc_v1` → { [itemId]: true }
 * "Need immediate help?" button navigates to /safety#hotlines and Safety.jsx
 * handles the scroll on mount.
 */

const STORAGE_KEY = "ss_lsc_v1";

const SECTIONS = [
  {
    id: "before",
    icon: Compass,
    title: "Before You Leave",
    items: [
      {
        id: "tell_one",
        label: "Tell one trusted person what's happening.",
        why: "Having one person who knows your plan means someone can check on you if communication suddenly stops.",
      },
      {
        id: "where_stay",
        label: "Decide where you'll stay for the first few nights.",
        why: "Planning ahead reduces the chance you'll need to return because you have nowhere safe to go.",
      },
      {
        id: "charged",
        label: "Keep your phone charged and carry a charger or power bank.",
        why: "Your phone may become your lifeline if you need help unexpectedly.",
      },
      {
        id: "documents",
        label: "Gather important documents if it's safe.",
        subLabel: "Examples of what to look for:",
        subItems: [
          "ID",
          "Passport",
          "Birth certificates",
          "Social Security cards",
          "Insurance cards",
          "Bank information",
          "Medications",
          "Keys",
        ],
        why: "Replacing important documents later can be difficult and may delay rebuilding your independence.",
      },
      {
        id: "bag",
        label: "Pack an emergency bag if it's safe.",
        why: "A prepared bag allows you to leave quickly if circumstances suddenly change.",
      },
      {
        id: "numbers_offline",
        label: "Save important phone numbers somewhere besides your phone.",
        why: "If your phone is lost, damaged, or taken, you'll still have access to important contacts.",
      },
    ],
  },
  {
    id: "tech",
    icon: Smartphone,
    title: "Technology Safety",
    items: [
      {
        id: "passwords",
        label: "Change passwords for key accounts.",
        subLabel: "Include:",
        subItems: ["Email", "Banking", "Apple / Google account", "Social media"],
        why: "Many abusive partners already know passwords or security questions.",
      },
      {
        id: "2fa",
        label: "Turn on two-factor authentication.",
        why: "This adds another layer of protection even if someone knows your password.",
      },
      {
        id: "location",
        label: "Turn off location sharing.",
        subLabel: "Common places to check:",
        subItems: [
          "Find My",
          "Google Maps",
          "Snapchat",
          "Facebook",
          "Life360",
          "Shared Calendars",
        ],
        why: "Location sharing can unintentionally reveal your whereabouts after you've left.",
      },
      {
        id: "devices",
        label: "Review devices logged into your accounts.",
        why: "Old devices may still have access to your email or personal information.",
      },
      {
        id: "trackers",
        label: "Check for AirTags or unknown Bluetooth trackers.",
        why: "Some survivors continue being tracked without realizing it.",
      },
      {
        id: "emergency_contacts",
        label: "Update emergency contacts.",
        why: "Ensure trusted people — not your abusive partner — are contacted during emergencies.",
      },
    ],
  },
  {
    id: "financial",
    icon: Banknote,
    title: "Financial Safety",
    items: [
      {
        id: "own_account",
        label: "Open a bank account only you control (if safe).",
        why: "Financial independence can provide greater flexibility and safety.",
      },
      {
        id: "photo_docs",
        label: "Photograph important financial documents.",
        why: "Digital copies may help if physical documents are lost.",
      },
      {
        id: "freeze_credit",
        label: "Freeze your credit if identity theft is a concern.",
        why: "Some abusive partners open accounts in their partner's name.",
      },
      {
        id: "bank_pins",
        label: "Change banking passwords and PINs.",
        why: "Shared information can remain accessible long after separation.",
      },
    ],
  },
  {
    id: "home",
    icon: HomeIcon,
    title: "Home Safety",
    items: [
      {
        id: "locks",
        label: "Consider changing locks if appropriate.",
        why: "Only if legally permitted and safe in your situation.",
      },
      {
        id: "garage",
        label: "Consider changing garage codes.",
        why: "Old access codes may still work.",
      },
      {
        id: "neighbors",
        label: "Tell trusted neighbors if appropriate.",
        why: "Extra eyes nearby can increase safety.",
      },
      {
        id: "curtains",
        label: "Consider keeping curtains closed if you're concerned about being watched.",
        why: "Reducing visibility may help protect your privacy.",
      },
    ],
  },
  {
    id: "kids_pets",
    icon: Users,
    title: "Children & Pets",
    items: [
      {
        id: "school_pickup",
        label: "Tell the school who is authorized for pickup.",
        why: "Schools often need updated custody and emergency information.",
      },
      {
        id: "kids_emergency",
        label: "Update emergency contacts.",
        why: "Ensures trusted people are contacted if needed.",
      },
      {
        id: "pet_plan",
        label: "Make a safety plan for pets.",
        why: "Abusers sometimes threaten or harm pets to maintain control.",
      },
    ],
  },
  {
    id: "emotional",
    icon: Heart,
    title: "Emotional Safety",
    footer:
      "These feelings are common after leaving. They do not mean leaving was the wrong decision.",
    items: [
      { id: "manip", label: "Expect manipulation." },
      { id: "promise", label: "Expect promises to change." },
      { id: "guilt", label: "Expect guilt." },
      { id: "question", label: "Expect moments where you question yourself." },
    ],
  },
  {
    id: "contact",
    icon: MessageSquare,
    title: "If They Contact You",
    items: [
      {
        id: "save_msgs",
        label: "Save threatening messages.",
        why: "Documentation may become important later.",
      },
      {
        id: "no_meet_alone",
        label: "Don't meet alone.",
        why: "Meeting in public or with support reduces risk.",
      },
      {
        id: "public_only",
        label: "Meet only in public if absolutely necessary.",
        why: "Public places are generally safer than private ones.",
      },
      {
        id: "call_911",
        label: "Call emergency services immediately if you believe you're in danger.",
      },
    ],
  },
  {
    id: "after",
    icon: Sun,
    title: "After You Leave",
    items: [
      {
        id: "employer",
        label: "Consider telling your employer.",
        why: "Your workplace may be able to screen calls, notify security, or help protect your schedule.",
      },
      {
        id: "school",
        label: "Consider telling your child's school.",
        why: "This helps staff better protect your child.",
      },
      {
        id: "advocate",
        label: "Consider talking with an advocate or therapist.",
        why: "Support can reduce isolation and help you heal.",
      },
      {
        id: "digital_privacy",
        label: "Continue checking your digital privacy.",
        why: "Technology settings change and new devices may reconnect accounts.",
      },
      {
        id: "celebrate",
        label: "Celebrate every safe day.",
        why: "Leaving isn't one event — it's a process. Every safe day matters.",
      },
    ],
  },
];

function itemKey(sectionId, itemId) {
  return `${sectionId}:${itemId}`;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function ChecklistItem({ sectionId, item, checked, onToggle }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const key = itemKey(sectionId, item.id);
  const isChecked = !!checked[key];

  return (
    <li className="rounded-2xl bg-brand-soft/15 border border-border p-3">
      <label
        className="flex items-start gap-3 cursor-pointer min-h-[44px]"
        data-testid={`lsc-item-${key}`}
      >
        <span
          className={`shrink-0 mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
            isChecked ? "bg-brand border-brand" : "bg-background border-border"
          }`}
          aria-hidden="true"
        >
          {isChecked && (
            <svg viewBox="0 0 20 20" className="w-4 h-4 text-white" fill="currentColor">
              <path d="M7.5 14.5l-4-4 1.4-1.4 2.6 2.6 6.6-6.6 1.4 1.4z" />
            </svg>
          )}
        </span>
        <input
          type="checkbox"
          className="sr-only"
          checked={isChecked}
          onChange={() => onToggle(key)}
          data-testid={`lsc-cb-${key}`}
          aria-label={item.label}
        />
        <span
          className={`text-[15px] leading-snug ${
            isChecked ? "text-foreground/50 line-through" : "text-foreground/85"
          }`}
        >
          {item.label}
        </span>
      </label>

      {item.subItems && (
        <div className="mt-2 ml-9">
          <button
            type="button"
            onClick={() => setSubOpen((v) => !v)}
            className="text-xs text-brand hover:underline underline-offset-2 inline-flex items-center gap-1"
            data-testid={`lsc-sub-toggle-${key}`}
            aria-expanded={subOpen}
          >
            {subOpen ? "Hide list" : `Show list (${item.subItems.length})`}
            {subOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {subOpen && (
            <div
              className="mt-2 rounded-xl bg-card border border-border p-3 text-sm text-foreground/75"
              data-testid={`lsc-sub-${key}`}
            >
              {item.subLabel && (
                <p className="text-xs uppercase tracking-widest text-foreground/55 mb-2">
                  {item.subLabel}
                </p>
              )}
              <ul className="space-y-1 list-disc pl-4">
                {item.subItems.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {item.why && (
        <div className="mt-2 ml-9">
          <button
            type="button"
            onClick={() => setWhyOpen((v) => !v)}
            className="text-xs text-brand hover:underline underline-offset-2 inline-flex items-center gap-1"
            data-testid={`lsc-why-toggle-${key}`}
            aria-expanded={whyOpen}
          >
            {whyOpen ? "Hide why this matters" : "Why this matters"}
            {whyOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {whyOpen && (
            <p
              className="mt-2 text-sm text-foreground/70 leading-relaxed italic"
              data-testid={`lsc-why-${key}`}
            >
              {item.why}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function ChecklistSection({ section, checked, onToggle, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = section.icon;
  const total = section.items.length;
  const done = section.items.filter(
    (it) => checked[itemKey(section.id, it.id)]
  ).length;

  return (
    <section
      className="rounded-3xl bg-card border border-border overflow-hidden"
      data-testid={`lsc-section-${section.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`lsc-section-toggle-${section.id}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-brand-soft/20 transition-colors min-h-[56px]"
      >
        <div className="w-9 h-9 rounded-xl bg-brand-soft/60 flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg leading-tight">{section.title}</p>
          <p className="text-xs text-foreground/55 mt-0.5">
            {done} of {total} checked
          </p>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-foreground/45 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-foreground/45 shrink-0" />
        )}
      </button>

      <div
        className={`grid transition-all duration-400 ease-in-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <ul className="px-4 pb-4 pt-1 space-y-2">
            {section.items.map((item) => (
              <ChecklistItem
                key={item.id}
                sectionId={section.id}
                item={item}
                checked={checked}
                onToggle={onToggle}
              />
            ))}
          </ul>
          {section.footer && (
            <p
              className="mx-5 mb-5 mt-1 text-sm italic text-foreground/70 leading-relaxed"
              data-testid={`lsc-section-footer-${section.id}`}
            >
              {section.footer}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function LeavingSafelyChecklist() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(loadState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
    } catch {
      /* localStorage unavailable — silently skip */
    }
  }, [checked]);

  const onToggle = (id) => {
    lightTap();
    setChecked((c) => {
      const next = { ...c };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  return (
    <AppShell>
      <div
        className="px-6 sm:px-8 pt-14 pb-6"
        data-testid="page-leaving-safely-checklist"
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-3 min-h-[44px]"
          data-testid="lsc-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <p className="font-script text-2xl text-brand">a step at a time</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">
          Leaving Safely Checklist
        </h1>
        <p className="mt-4 text-foreground/75 leading-relaxed">
          Practical steps for safely ending or leaving a relationship. Move at your own pace —
          this is a private space and everything is saved on your device.
        </p>
      </div>

      <div className="px-6 sm:px-8 space-y-3 pb-8">
        {SECTIONS.map((s, i) => (
          <ChecklistSection
            key={s.id}
            section={s}
            checked={checked}
            onToggle={onToggle}
            defaultOpen={i === 0}
          />
        ))}
      </div>

      {/* Reassurance card */}
      <div className="px-6 sm:px-8 pb-6">
        <div
          className="rounded-3xl bg-brand/8 border border-brand/20 p-6"
          data-testid="lsc-remember"
        >
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-brand mt-1 shrink-0" />
            <div className="space-y-2 text-foreground/85 leading-relaxed">
              <p className="font-serif text-lg">Remember</p>
              <p>You do not have to complete everything perfectly.</p>
              <p>Every small safety step increases your protection.</p>
              <p>Leaving is a process, not a single moment.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-24">
        <button
          type="button"
          onClick={() => navigate("/safety#hotlines")}
          data-testid="lsc-immediate-help"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand text-white hover:bg-brand/90 transition-all min-h-[52px]"
        >
          <Phone className="w-4 h-4" />
          Need immediate help?
        </button>
      </div>
    </AppShell>
  );
}
